import { deleteCreditConfiguration, getCreditConfiguration, updateCreditConfiguration, addCreditConfiguration } from 'apis/adminServices/creditServices';
import React, { useState, useEffect, memo, useCallback } from 'react';
import SearchBar from '../searchbar';
import Pagination from '../pagination';
import CreditTable from './creditTable';
import AddCreditModal from './addCreditModel';
import Toast from 'components/Toast';
import { useToastStore } from 'store/toastStore';

interface CreditConfigAPI {
    creditManagementId: number;
    configurationName: string;
    createdDate: string;
    status: 'Active' | 'Disabled';
    answer: number;
    request: number;
    invite: number;
    signup: number;
    initialFive: number;
}

interface CreditConfig {
    id: number;
    name: string;
    date: string;
    status: 'Active' | 'Disabled';
    answer: number;
    request: number;
    invite: number;
    signup: number;
    initialFive: number;
}

interface Configuration {
    name: string;
    creditsForPostingLoad: number;
    creditsForAnsweringRequest: number;
    creditsForInitialSignup: number;
    creditsForFirstFiveAnswers: number;
    description: string;
    creditsForReferrals: number;
    isActive: boolean;
}

const EMPTY_CREDIT: Configuration = {
    name: '',
    creditsForPostingLoad: 0,
    creditsForAnsweringRequest: 0,
    creditsForInitialSignup: 0,
    creditsForFirstFiveAnswers: 0,
    description: '',
    creditsForReferrals: 0,
    isActive: true,
};

interface CreditConfigResponse {
    totalRecords: number;
    data: CreditConfigAPI[];
}

const CreditSettings: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [credits, setCredits] = useState<CreditConfig[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [newCredit, setNewCredit] = useState<Configuration>({ ...EMPTY_CREDIT });
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { showToast } = useToastStore();
    const [pageSize, setPageSize] = useState(10);

    const transformApiData = (apiData: CreditConfigAPI[]): CreditConfig[] => {
        return apiData.map(item => ({
            id: item.creditManagementId,
            name: item.configurationName,
            date: new Date(item.createdDate).toLocaleDateString('en-GB'),
            status: item.status,
            answer: item.answer,
            request: item.request,
            invite: item.invite,
            signup: item.signup,
            initialFive: item.initialFive
        }));
    };

    useEffect(() => {
        const fetchCreditConfigurations = async () => {
            try {
                setLoading(true);
                const response = await getCreditConfiguration({
                    SearchWithName: searchTerm,
                    PageNumber: currentPage,
                    PageSize: pageSize
                }) as CreditConfigResponse;
                // If this page is now empty (e.g. the last record on it was
                // deleted) but earlier pages still have records, step back one
                // page — the effect refetches — instead of stranding the user
                // on an empty "No configurations found" page.
                if (response.data.length === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                    return;
                }
                const transformedData = transformApiData(response.data);
                setCredits(transformedData);
                setTotalRecords(response.totalRecords);
                setError(null);
            } catch (err) {
                setError('Failed to fetch credit configurations');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchCreditConfigurations();
    }, [searchTerm, currentPage, pageSize]);

    const handleDelete = useCallback(async (id: number): Promise<string> => {
        setLoading(true);
        try {
            await deleteCreditConfiguration({ id });
            const fetchResponse = await getCreditConfiguration({
                SearchWithName: searchTerm,
                PageNumber: currentPage,
                PageSize: pageSize
            }) as CreditConfigResponse;
            // Deleted the last record on this page → drop back to the previous
            // page (the effect refetches it) instead of showing an empty page.
            if (fetchResponse.data.length === 0 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
                setLoading(false);
                return "Configuration deleted successfully";
            }
            const transformedData = transformApiData(fetchResponse.data);
            setCredits(transformedData);
            setTotalRecords(fetchResponse.totalRecords);
            setError(null);
            setLoading(false);
            return "Configuration deleted successfully";
        } catch (error: any) {
            setLoading(false);
            const errorMessage = error.response?.data || 'Failed to delete configuration';
            return errorMessage;
        }
    }, [setLoading, setCredits, setTotalRecords, searchTerm, currentPage, setError]);

    const handleUpdate = useCallback(async (updatedCredit: CreditConfig) => {
        setLoading(true);
        try {
            const fetchResponse = await getCreditConfiguration({
                SearchWithName: searchTerm,
                PageNumber: currentPage,
                PageSize: pageSize
            }) as CreditConfigResponse;
            const transformedData = transformApiData(fetchResponse.data);
            setCredits(transformedData);
            setTotalRecords(fetchResponse.totalRecords);
            setError(null);
        } catch (err) {
            setError('Failed to refresh credit configurations after update');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [setLoading, setCredits, setTotalRecords, searchTerm, currentPage, setError]);

    const handleAddCredit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setLoading(true);
        try {
            const apiData = {
                configName: newCredit.name,
                answer: newCredit.creditsForAnsweringRequest,
                request: newCredit.creditsForPostingLoad,
                signup: newCredit.creditsForInitialSignup,
                initialFive: newCredit.creditsForFirstFiveAnswers,
                referrals: newCredit.creditsForReferrals,
                description: newCredit.description,
                isActive: newCredit.isActive,
            };
            await addCreditConfiguration(apiData);

            const fetchResponse = await getCreditConfiguration({
                SearchWithName: searchTerm,
                PageNumber: currentPage,
                PageSize: pageSize
            }) as CreditConfigResponse;
            const transformedData = transformApiData(fetchResponse.data);
            setCredits(transformedData);
            setTotalRecords(fetchResponse.totalRecords);
            setError(null);

            showToast(`Configuration '${newCredit.name}' has been added successfully.`, 'success');
            setNewCredit({ ...EMPTY_CREDIT });
            setIsModalOpen(false);
        } catch (error: any) {
            const errorMessage = error.response?.data || 'Failed to add configuration';
            showToast(errorMessage, 'error');
            console.error('Error adding configuration:', error);
        } finally {
            setIsSubmitting(false);
            setLoading(false);
        }
    }, [newCredit, setNewCredit, setLoading, setCredits, setTotalRecords, setError, showToast, searchTerm, currentPage]);

    return (
        <section className="bg-white p-6 rounded-xl mt-6">
            <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
                <p className="text-grease-100 text-sm font-semibold">Token Settings</p>
                <div className="flex flex-wrap items-center gap-3">
                    <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                    <button
                        type="button"
                        onClick={() => {
                            setNewCredit({ ...EMPTY_CREDIT });
                            setError(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center justify-center gap-2 p-3 border bg-[#FFD700] rounded-lg hover:bg-[#E6C200] transition-colors"
                    >
                        <span className="st-add text-white" />
                        <p className="font-medium text-white text-sm">Add New</p>
                    </button>
                </div>
            </div>

            {error && (
                <div className="text-center py-4 text-red-600">
                    {error}
                    <button onClick={() => setError(null)} className="ml-2 text-blue-600">Dismiss</button>
                </div>
            )}
            <CreditTable 
                credits={credits} 
                onDelete={handleDelete} 
                loading={loading} 
                onUpdate={handleUpdate}
            />
            <Toast />
            <Pagination
                currentPage={currentPage}
                totalItems={totalRecords}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
            />
            {isModalOpen && (
                <AddCreditModal
                    newCredit={newCredit}
                    setNewCredit={setNewCredit}
                    onAdd={handleAddCredit}
                    onClose={() => {
                        setNewCredit({ ...EMPTY_CREDIT });
                        setIsModalOpen(false);
                    }}
                    isSubmitting={isSubmitting}
                />
            )}
        </section>
    );
};

export default memo(CreditSettings);